import { clsx } from 'clsx'
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'

import { FvButton, ValidatedForm } from '@fv/client-components'
import type { ShipmentLocation } from '@fv/client-types'
import type { LocationType } from '@fv/models'
import type { EquipmentAccessorial } from '@fv/models/equipment'

import { FooterBar } from '@/components/FooterBar'
import { InputGroupWrapper } from '@/components/inputs/InputGroup'
import { Loading } from '@/components/Loading'
import { useCarrierPublicSettings } from '@/hooks/useCarrierPublicSettings'
import {
  defaultStopDate,
  getCustomsBrokerLocationContext,
} from '@/utils/shipmentFuncs'

import { useRole } from '../auth/hooks'
import { loadCanBeBooked } from '../book/bookFuncs'
import { PickupOnlyPrompt } from '../booking/PickupOnlyPrompt'
import {
  LoadItemsForm,
  type LoadItemsFormRef,
} from '../commodities/load-items/LoadItemsFormProvider'
import {
  defaultLoadHandlingUnit,
  unitIsHazardous,
} from '../commodities/load-items/loadItemHelpers'
import { ShipmentBrokerFormSection } from '../customsBroker/ShipmentBrokerFormSection'
import { EmergencyContactFormSection } from '../emergencyContact/EmergencyContactFormSection'
import { GLCodeSelector } from '../gl-codes/GLCodeSelector'
import { useAccountSettings } from '../settings/account-settings/hooks'
import { ShipmentEquipmentForm } from '../shipment-equipment-form/ShipmentEquipmentForm'
import type { ShipmentEquipmentFormModel } from '../shipment-equipment-form/types'
import {
  defaultDestination,
  defaultOrigin,
} from '../shipment-location-form/types'
import { useShipLocFormModelBuilder } from '../shipment-location-form/useLocFormModelBuilder'
import { AutofillPreviousShipment } from './AutofillPreviousShipment'
import { LtlAccessorialsFormSection } from './LtlAccessorialsFormSection'
import { ShipmentBolFormField } from './ShipmentBolFormField'
import { ShipmentBookActions } from './ShipmentBookActions'
import { ShipmentFormLocations } from './ShipmentFormLocations'
import { ShipmentFormSection } from './ShipmentFormSection'
import {
  useDefaultEquipmentBuilder,
  useShipmentFormActions,
  useShipmentFormState,
} from './store'
import type { ShipmentFormModel } from './types'
import {
  insertStopAfter,
  removeStop,
  shiftItemStopsForInsert,
  shiftItemStopsForRemoval,
  toItemStops,
} from './utils'

type Props = {
  isSaving: boolean
  canSkipItems?: boolean
  /** A copy already carries the source shipment's values, so remembered autofill mustn't overwrite them. */
  skipInitialAutofill?: boolean
  onSave?: (model: ShipmentFormModel) => void
  onPrintBOL?: (model: ShipmentFormModel) => void
  onSchedulePickup?: (model: ShipmentFormModel) => void
  onCreate?: (model: ShipmentFormModel) => void
}

export interface ShipmentFormHandle {
  reset: () => void
}

export const ShipmentForm = forwardRef<ShipmentFormHandle, Props>(
  (props: Props, ref) => {
    /*  Setup */
    const {
      type,
      workflow,
      initialLoad,
      quote,
      locations,
      items,
      equipment: equip,
      emergencyContact: emergency,
      customsBrokers: broker,
      bolNum,
      glCode,
      schedulePickup,
      isPickupOnly,
      itemsFormKey,
      selectedTemplate,
    } = useShipmentFormState()
    const isBook = type === 'book'
    const isCreate = type === 'create'
    const isEdit = type === 'edit'
    const itemsFormRef = useRef<LoadItemsFormRef>(null)
    const formRef = useRef<HTMLFormElement>(null)
    const carrierSettingsQuery = useCarrierPublicSettings(quote?.carrierId)
    const accountSettings = useAccountSettings()
    const role = useRole()
    /* / Setup */

    const {
      setLocations,
      setItems,
      setEquipment: setEquip,
      setEmergencyContact: setEmergency,
      setCustomsBrokers: setBroker,
      setBolNum,
      setGlCode,
      setSchedulePickup,
      setPickupOnly,
      clear,
    } = useShipmentFormActions()
    const { build, isLoading: isLoadingAddresses } = useShipLocFormModelBuilder(
      initialLoad ?? { workflow },
    )
    const buildDefaultEquipment = useDefaultEquipmentBuilder()

    /* Computed */
    const isLoading = isLoadingAddresses
    const showBookingActions = isBook && loadCanBeBooked(initialLoad)
    const { required: requireBroker, location: internationalLocation } =
      getCustomsBrokerLocationContext(locations)
    const isHazard = items.some(unitIsHazardous)
    const requireOriginEmail =
      isBook && carrierSettingsQuery.carrierPublicSettings?.requiresOriginEmail
    const skipItems = !!props.canSkipItems && isPickupOnly
    const canEditStops =
      isCreate && workflow === 'truckload' && role !== 'vendor'
    // Booking a pickup-only multi-stop load is when its items first get assigned to stops.
    const itemStops = useMemo(
      () => (workflow === 'truckload' ? toItemStops(locations) : undefined),
      [workflow, locations],
    )

    const buildFullModel = (): ShipmentFormModel => ({
      items,
      locations,
      bolNum,
      glCode,
      schedulePickup,
      equipment: equip,
      workflow,
      isPickupOnly: skipItems,
      selectedTemplate,
      ...(internationalLocation && { customsBrokers: broker }),
      ...(isHazard && { emergencyContact: emergency }),
    })
    /* /Computed */

    /* Handlers */
    const handleSubmit = async () => {
      if (isCreate && props.onCreate) {
        props.onCreate(buildFullModel())
      } else if (isEdit && props.onSave) {
        props.onSave(buildFullModel())
      } else if (isBook && props.onSchedulePickup) {
        props.onSchedulePickup(buildFullModel())
      }
    }
    const handlePrevLocChange =
      (type: LocationType) =>
      (val: ShipmentLocation | undefined, enabled: boolean) => {
        const newVal =
          enabled && val
            ? build(val)
            : type === 'origin'
              ? defaultOrigin()
              : defaultDestination()
        setLocations(prev =>
          prev.map(l =>
            l.type === type
              ? {
                  ...newVal,
                  sequence: l.sequence,
                  ...(type === 'origin' && {
                    stopDate: defaultStopDate(type),
                    refNum: '',
                  }),
                  ...(type === 'destination' && {
                    stopDate: null,
                  }),
                }
              : l,
          ),
        )
      }

    const updateItems = (next: typeof items) => {
      setItems(next)
      itemsFormRef.current?.setItems(next)
    }
    const handleAddStop = (afterSequence: number) => {
      setLocations(prev => insertStopAfter(prev, afterSequence))
      updateItems(shiftItemStopsForInsert(items, afterSequence))
    }
    const handleRemoveStop = (sequence: number) => {
      setLocations(prev => removeStop(prev, sequence))
      updateItems(shiftItemStopsForRemoval(items, sequence))
    }

    const handlePrevEquipChange = (
      recentEquipment: ShipmentEquipmentFormModel[],
      enabled: boolean,
    ) => {
      const value = recentEquipment[0]
      const newVal = enabled && value ? value : buildDefaultEquipment(workflow)
      setEquip(prev => ({
        ...prev,
        type: newVal.type,
        alternateTypes: newVal.alternateTypes ?? [],
        accessorials: newVal.accessorials ?? [],
      }))
    }
    /* /Handlers */

    useImperativeHandle(ref, () => ({
      reset: () => {
        clear()
        itemsFormRef.current?.setItems([defaultLoadHandlingUnit(workflow)])
        formRef.current?.reset()
      },
    }))

    if (isLoading) {
      return <Loading />
    }

    return (
      <>
        {isCreate && (
          <div className="bg-fv-blue-50 border-fv-blue-300 flex items-center border-b border-dashed px-6 py-4">
            <AutofillPreviousShipment
              workflow={workflow}
              selectedTemplateId={selectedTemplate?._id}
              skipInitialAutofill={props.skipInitialAutofill}
              handlers={{
                origin: handlePrevLocChange('origin'),
                destination: handlePrevLocChange('destination'),
                ...(!skipItems && {
                  // Stop assignments belong to the previous shipment's route, not this one.
                  product: (recentItems, enabled) =>
                    updateItems(
                      enabled
                        ? recentItems.map(
                            ({
                              pickupLocationSequence,
                              dropLocationSequence,
                              ...item
                            }) => item,
                          )
                        : [defaultLoadHandlingUnit(workflow)],
                    ),
                }),
                ...(workflow === 'truckload' && {
                  equipment: handlePrevEquipChange,
                }),
              }}
            />
          </div>
        )}
        <ValidatedForm ref={formRef} onValidSubmit={handleSubmit}>
          <div
            className={clsx(
              'p-6 bg-white overflow-auto flex flex-col gap-y-12',
              !props.canSkipItems && 'pt-10',
            )}
          >
            {props.canSkipItems && (
              <PickupOnlyPrompt
                checked={isPickupOnly}
                className="!mb-0"
                onChange={e => setPickupOnly(e.currentTarget.checked)}
              />
            )}
            <ShipmentFormLocations
              onSchedulePickupChange={
                type !== 'book' && workflow === 'parcel'
                  ? setSchedulePickup
                  : undefined
              }
              schedulePickup={schedulePickup}
              workflow={workflow}
              value={locations}
              onChange={setLocations}
              requireOriginEmail={requireOriginEmail}
              onAddStop={canEditStops ? handleAddStop : undefined}
              onRemoveStop={canEditStops ? handleRemoveStop : undefined}
            />
            {workflow === 'truckload' && (
              <ShipmentEquipmentForm
                value={equip}
                onChange={setEquip}
                showLoadSummary={skipItems && !isBook}
              />
            )}
            {workflow === 'ltl' && (
              <LtlAccessorialsFormSection
                disabled={props.isSaving}
                value={equip.accessorials.map(a => a.key)}
                onChange={(key, checked) => {
                  const accessorials = equip.accessorials.filter(
                    a => a.key !== key,
                  )
                  setEquip({
                    ...equip,
                    accessorials: checked
                      ? accessorials.concat({ key } as EquipmentAccessorial)
                      : accessorials,
                  })
                }}
              />
            )}

            {!skipItems && (
              <LoadItemsForm
                key={itemsFormKey}
                ref={itemsFormRef}
                workflow={workflow}
                initialItems={items}
                step="booking"
                type="inline"
                onChange={setItems}
                stops={itemStops}
              />
            )}

            {!skipItems && isHazard && (
              <EmergencyContactFormSection
                value={emergency}
                onChange={setEmergency}
              />
            )}
            {!skipItems && internationalLocation && (
              <ShipmentBrokerFormSection
                workflow="ltl"
                loadLocation={internationalLocation}
                locations={locations}
                value={[broker]}
                onChange={v => setBroker(v[0])}
                requireDeclaredValue={false} // TODO VP
                required={requireBroker}
              />
            )}
            <ShipmentFormSection className="gray-container flex gap-x-2 !mb-0">
              <ShipmentBolFormField
                className={
                  accountSettings?.glCode?.enabled
                    ? 'basis-1/2 mb-0'
                    : 'basis-full mb-0'
                }
                value={bolNum}
                onChange={setBolNum}
                loadId={initialLoad?.loadId}
              />
              {accountSettings?.glCode?.enabled && (
                <InputGroupWrapper
                  className="basis-1/2 mb-0"
                  label="GL Code"
                  name="glCode"
                  required={accountSettings.glCode.required}
                >
                  <GLCodeSelector
                    value={glCode}
                    onChange={value => setGlCode(value ?? '')}
                    required={accountSettings.glCode.required}
                  />
                </InputGroupWrapper>
              )}
            </ShipmentFormSection>
          </div>
          <FooterBar jc>
            {(isCreate || isEdit) && (
              <FvButton
                type="submit"
                theme="primary"
                icon="check"
                fwd
                loading={props.isSaving}
              >
                {isCreate ? 'Create shipment' : 'Update shipment'}
              </FvButton>
            )}
            {showBookingActions && (
              <ShipmentBookActions
                isLoading={props.isSaving}
                onPrintBOL={
                  skipItems
                    ? undefined
                    : () => props.onPrintBOL?.(buildFullModel())
                }
                items={items}
                locations={locations}
                workflow={workflow}
                quote={quote}
              />
            )}
          </FooterBar>
        </ValidatedForm>
      </>
    )
  },
)
